import { useEffect, useMemo, useState } from 'react';
import type { ContactInput, CrmContactCandidate } from '@stromo/shared';
import { Modal } from './Modal';
import { useCrmSearch } from '../lib/crm';
import { apiErrorMessage } from '../lib/api';

interface Props {
  open: boolean;
  onClose: () => void;
  /** Wird aufgerufen wenn ein Treffer übernommen wurde — der Parent öffnet
   *  dann das normale Contact-Form mit den vorausgefüllten Werten. */
  onPick: (input: ContactInput) => void;
}

/** Wandelt einen CRM-Kandidaten in das Stromo-Contact-Input-Format um.
 *  contact_types lassen wir bewusst auf ['other'] — User wählt den Typ
 *  im Folge-Modal selbst. */
function candidateToInput(c: CrmContactCandidate): ContactInput {
  // Anzeige-Name folgt der Braso-Logik: bei Firmen NAME1 (+ Zusatz),
  // bei Privatpersonen Vor- + Nachname.
  const name = c.display_name;
  // Adresse + ggf. Ansprechpartner in den Bemerkungen mitführen.
  const addressLine = [c.street, [c.zip, c.city].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(', ');
  const contactPersonLine = !c.is_private && (c.first_name || c.last_name)
    ? `Ansprechpartner: ${[c.first_name, c.last_name].filter(Boolean).join(' ')}`
    : null;
  const notes = [addressLine || null, contactPersonLine].filter(Boolean).join('\n') || null;
  return {
    name,
    contact_types: ['other'],
    email: c.email,
    phone: c.phone,
    notes,
    external_id: c.external_id,
    external_source: c.external_source,
  };
}

export function CrmImportModal({ open, onClose, onPick }: Props) {
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');

  // 300-ms-Debounce, damit Tippen nicht jedes Zeichen einen DB-Roundtrip auslöst.
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(query), 300);
    return () => clearTimeout(timer);
  }, [query]);

  // Beim Schliessen Eingabe zurücksetzen — sonst hängen alte Treffer drin.
  useEffect(() => {
    if (!open) {
      setQuery('');
      setDebounced('');
    }
  }, [open]);

  const search = useCrmSearch(debounced, open);

  const handlePick = (candidate: CrmContactCandidate) => {
    onPick(candidateToInput(candidate));
  };

  const placeholder = useMemo(
    () => 'Name, Firma oder Telefonnummer …',
    [],
  );

  return (
    <Modal
      open={open}
      title="Kontakt aus CRM importieren (Braso)"
      onClose={onClose}
      width={620}
      footer={
        <button type="button" className="btn btn-ghost" onClick={onClose}>
          Schliessen
        </button>
      }
    >
      <input
        autoFocus
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={placeholder}
        style={{ width: '100%', marginBottom: 12 }}
      />
      <div style={{ fontSize: 12, color: 'var(--color-text-soft)', marginBottom: 12 }}>
        Sucht parallel über Firma/Name und über alle hinterlegten Telefonnummern.
        Mindestens 2 Zeichen — Maximal 25 Treffer.
      </div>

      {debounced.length < 2 && (
        <div className="empty-state" style={{ padding: 24, fontSize: 13 }}>
          <p>Mind. 2 Zeichen eingeben …</p>
        </div>
      )}

      {debounced.length >= 2 && search.isLoading && (
        <div className="loading">Suche …</div>
      )}

      {search.isError && (
        <div className="login-error">{apiErrorMessage(search.error)}</div>
      )}

      {debounced.length >= 2 && !search.isLoading && (search.data ?? []).length === 0 && (
        <div className="empty-state" style={{ padding: 16 }}>
          <p>Keine Treffer für „{debounced}".</p>
        </div>
      )}

      {(search.data ?? []).some((c) =>
        [c.display_name, c.email, c.street, c.city].some((s) => s && s.includes('�')),
      ) && (
        <div
          style={{
            background: 'var(--color-warning-bg, #fef3c7)',
            color: 'var(--color-warning-text, #92590f)',
            padding: '8px 12px',
            borderRadius: 6,
            fontSize: 12,
            marginBottom: 10,
            border: '1px solid var(--color-warning-text, #92590f)',
          }}
        >
          ⚠ Umlaute werden vom CRM-Driver nicht korrekt geliefert (erscheinen als „�"). Bitte beim Import den Namen manuell korrigieren.
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 360, overflowY: 'auto' }}>
        {(search.data ?? []).map((c) => {
          const alreadyImported = c.already_imported_id !== null;
          return (
            <button
              key={c.external_id}
              type="button"
              onClick={() => handlePick(c)}
              disabled={alreadyImported}
              style={{
                textAlign: 'left',
                padding: '10px 14px',
                borderRadius: 8,
                border: '1px solid var(--color-border-soft)',
                background: alreadyImported ? 'var(--color-surface-alt)' : '#fff',
                cursor: alreadyImported ? 'not-allowed' : 'pointer',
                fontFamily: 'inherit',
                opacity: alreadyImported ? 0.7 : 1,
              }}
              title={alreadyImported ? 'Wurde bereits importiert' : 'Klick zum Übernehmen'}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
                <div style={{ fontWeight: 600, color: 'var(--color-navy)' }}>
                  {c.display_name}
                  {c.is_private && (
                    <span style={{ fontSize: 11, color: 'var(--color-text-soft)', marginLeft: 8, fontWeight: 400 }}>
                      (Privatperson)
                    </span>
                  )}
                </div>
                {alreadyImported && (
                  <span style={{ fontSize: 11, color: 'var(--color-success-text)', fontWeight: 600 }}>
                    ✓ bereits importiert
                  </span>
                )}
              </div>
              <div style={{ fontSize: 12.5, color: 'var(--color-text-soft)', marginTop: 3, display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                {c.email && <span>{c.email}</span>}
                {c.phone && <span>{c.phone}</span>}
                {(c.street || c.city) && <span>{[c.street, c.zip, c.city].filter(Boolean).join(' · ')}</span>}
              </div>
            </button>
          );
        })}
      </div>
    </Modal>
  );
}
